import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { Video, FileText, Briefcase, Plus, Eye, Trash2, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

export default function InterviewManagement() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    try {
      const q = query(collection(db, 'interviews'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const interviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInterviews(interviewsData);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInterview = () => {
    navigate('/admin/interview-management/create');
  };

  const handleViewReport = (interviewId, candidateId) => {
    navigate(`/admin/interview-management/report/${interviewId}/${candidateId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Interview Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">AI-powered candidate interviews</p>
        </div>
        <button
          onClick={handleCreateInterview}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Interview
        </button>
      </div>

      <div className="grid gap-6">
        {interviews.map((interview) => (
          <div key={interview.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {interview.positionTitle}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {interview.department} • {interview.experienceLevel}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                interview.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {interview.status}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm">{interview.candidates?.length || 0} Candidates</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Video className="w-4 h-4" />
                <span className="text-sm">AI Interview</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <FileText className="w-4 h-4" />
                <span className="text-sm">Auto Analysis</span>
              </div>
            </div>

            {interview.candidates && interview.candidates.length > 0 && (
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Candidates</h4>
                <div className="space-y-2">
                  {interview.candidates.map((candidate) => (
                    <div key={candidate.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{candidate.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {candidate.overallRating && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                              {candidate.overallRating}/100
                            </span>
                          )}
                          <button
                            onClick={() => handleViewReport(interview.id, candidate.id)}
                            className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Report
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">Interview Link:</span>
                        <input
                          type="text"
                          value={candidate.interviewLink}
                          readOnly
                          className="flex-1 text-xs bg-transparent border-none focus:outline-none text-blue-600 dark:text-blue-400"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(candidate.interviewLink);
                            toast.success('Link copied to clipboard!');
                          }}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {interviews.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
            <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No interviews yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first AI-powered interview
            </p>
            <button
              onClick={handleCreateInterview}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Interview
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
